import api from './api'

export interface NoteCategory {
  _id: string
  title: string
  type: string
  is_active: boolean
  createdAt: string
  updatedAt: string
  __v: number
  isDeleted: boolean
}

export interface TranscribeResponse {
  transcript?: string
  text?: string
  // API might return transcript directly as string or in an object
  [key: string]: any
}

export interface GenerateNoteRequest {
  noteType: string
  therapistSummary: string
  contractId: string
  sessionID: string
  diagnosis?: string[]
  cpt?: string[]
  hcpcs?: string[]
}

export interface GenerateNoteResponse {
  type?: string
  generatedNote?: string
  content?: string
  note?: string
  text?: string
  // API might return content directly or in different formats
  [key: string]: any
}

export interface SaveNoteRequest {
  contractId: string
  sessionId: string
  content: string
  noteCategory: string
  visibleto_patient: boolean
  status: 'draft' | 'finalized'
  diagnosis?: string[]
  cpt?: string[]
  hcpcs?: string[]
}

export interface SaveNoteResponse {
  _id: string
  contractId: string
  sessionId: string
  content: string
  noteCategory: string | NoteCategory
  visibleto_patient: boolean
  status?: string
  createdAt: string
  updatedAt: string
  // Add other fields if API returns more
}

export interface UpdateNoteRequest {
  contractId: string
  sessionId: string
  content: string
  noteCategory: string
  visibleto_patient: boolean
  status: 'draft' | 'finalized'
  diagnosis?: string[]
  cpt?: string[]
  hcpcs?: string[]
}

export interface NoteDetailResponse
  extends Omit<SaveNoteResponse, 'noteCategory'> {
  patientId?: string
  therapistId?: string
  isActive?: boolean
  isDeleted?: boolean
  noteCategory?: NoteCategory
  pdf_url?: string
}

export interface NoteListItem {
  _id: string
  contractId: string
  sessionId: string
  patientId?: string
  therapistId?: string
  content: string
  pdf_url?: string
  noteCategory?: NoteCategory
  status: string
  isActive?: boolean
  visibleto_patient: boolean
  isDeleted?: boolean
  createdAt: string
  updatedAt: string
  __v?: number
}

export interface NotesListResponse {
  data: NoteListItem[]
}

// Notes API functions
export const notesApi = {
  // Get list of note categories
  getNoteCategories: async (): Promise<NoteCategory[]> => {
    const response = await api.get<NoteCategory[]>('/notes/note-category')
    return response.data
  },

  // Transcribe audio file
  transcribeAudio: async (
    audioFile: File,
    patientId: string
  ): Promise<TranscribeResponse> => {
    const formData = new FormData()
    formData.append('audio', audioFile)
    formData.append('patientId', patientId)

    const response = await api.post<TranscribeResponse>(
      '/notes/transcribe',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 seconds for transcription
      }
    )
    // Handle different response formats
    const data = response.data
    if (typeof data === 'string') {
      return { transcript: data }
    }
    // Extract transcript as string, ensuring we don't assign the whole object
    const transcript =
      typeof data.transcript === 'string'
        ? data.transcript
        : typeof data.text === 'string'
          ? data.text
          : ''
    return {
      transcript,
      ...data,
    }
  },

  // Generate note using AI
  generateNote: async (
    request: GenerateNoteRequest
  ): Promise<GenerateNoteResponse> => {
    const response = await api.post<GenerateNoteResponse>(
      '/notes/generate',
      request,
      {
        timeout: 0, // No timeout - wait indefinitely
      }
    )
    // Handle different response formats
    const data = response.data
    if (typeof data === 'string') {
      return { content: data }
    }
    // Handle the new format with generatedNote
    if (data.generatedNote) {
      const generatedNote =
        typeof data.generatedNote === 'string' ? data.generatedNote : ''
      return {
        type: data.type,
        generatedNote,
        content: generatedNote, // Also set content for backward compatibility
        ...data,
      }
    }
    // Extract content as string, ensuring we don't assign the whole object
    const content =
      typeof data.content === 'string'
        ? data.content
        : typeof data.note === 'string'
          ? data.note
          : typeof data.text === 'string'
            ? data.text
            : ''
    return {
      content,
      ...data,
    }
  },

  // Save note
  saveNote: async (request: SaveNoteRequest): Promise<SaveNoteResponse> => {
    const response = await api.post<SaveNoteResponse>('/notes', request)
    return response.data
  },

  // Get note by ID
  getNoteById: async (noteId: string): Promise<NoteDetailResponse> => {
    const response = await api.get<NoteDetailResponse>(`/notes/${noteId}`)
    return response.data
  },

  // Update note
  updateNote: async (
    noteId: string,
    request: UpdateNoteRequest
  ): Promise<SaveNoteResponse> => {
    const response = await api.put<SaveNoteResponse>(
      `/notes/${noteId}`,
      request
    )
    return response.data
  },

  // Delete note
  deleteNote: async (noteId: string): Promise<void> => {
    await api.delete(`/notes/${noteId}`)
  },

  // Get notes by contractId and sessionId
  getNotesByContractAndSession: async (
    contractId: string,
    sessionId: string
  ): Promise<NoteListItem[]> => {
    const response = await api.get<NotesListResponse>(
      `/notes?contractId=${contractId}&sessionId=${sessionId}`
    )
    return response.data.data || []
  },
}
